/**
 * Single Parameter Sensitivity Mock
 *
 * Shows a sensitivity sweep for one parameter across multiple
 * metrics: Sharpe ratio, profit factor, and max drawdown.
 * Highlights the "stable zone" on the chart.
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
} from 'recharts';

const data = [
  { param: 10, sharpe: 0.4 },
  { param: 12, sharpe: 0.6 },
  { param: 14, sharpe: 0.9 },
  { param: 16, sharpe: 1.1 },
  { param: 18, sharpe: 1.25 },
  { param: 20, sharpe: 1.3 },
  { param: 22, sharpe: 1.35 },
  { param: 24, sharpe: 1.28 },
  { param: 26, sharpe: 1.15 },
  { param: 28, sharpe: 0.9 },
  { param: 30, sharpe: 0.6 },
];

const tipStyle = {
  background: 'rgba(17,24,39,0.95)',
  border: '1px solid rgba(75,85,99,0.5)',
  borderRadius: 6,
  fontSize: 11,
  fontFamily: 'ui-monospace, monospace',
};

export default function SingleParamSensitivityMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Single-Parameter Sensitivity &mdash; EMA Period
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="#334155" strokeWidth={0.5} vertical={false} />
            <XAxis
              dataKey="param"
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              domain={[0, 1.6]}
              label={{ value: 'Sharpe', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 9, offset: 10 }}
            />
            <Tooltip
              contentStyle={tipStyle}
              formatter={(v: number) => [v.toFixed(2), 'Sharpe']}
              labelFormatter={(l) => `EMA Period: ${l}`}
              cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <ReferenceArea x1={16} x2={26} fill="#22c55e" fillOpacity={0.06} stroke="#22c55e" strokeOpacity={0.2} strokeDasharray="3 2" label={{ value: 'Stable Zone', fill: '#22c55e', fontSize: 10, position: 'insideTop' }} />
            <Line type="monotone" dataKey="sharpe" stroke="#3b82f6" strokeWidth={1.5} dot={{ r: 2.5, fill: '#3b82f6' }} activeDot={{ r: 4, fill: '#3b82f6' }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '0.375rem', fontSize: '0.5625rem' }}>
          <span style={{ color: '#94a3b8' }}>
            Plateau width: <strong style={{ color: '#22c55e' }}>6 values</strong> (16-26)
          </span>
          <span style={{ color: '#94a3b8' }}>
            Peak Sharpe: <strong style={{ color: '#e5e7eb' }}>1.35</strong>
          </span>
          <span style={{ color: '#94a3b8' }}>
            Min in zone: <strong style={{ color: '#e5e7eb' }}>1.10</strong>
          </span>
        </div>
      </div>
      <div className="platform-mock-caption">
        The stable zone spans 6 parameter values — all producing Sharpe ratios above 1.1.
      </div>
    </div>
  );
}
